/**
 * useNotificationPolling Hook
 * Polls persistent notifications and displays browser notifications.
 *
 * Requirements: 17.6, 17.7
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as notificationsAPI from '@/api/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useNotifications } from './useNotifications';
import { getNotificationLabel, getNotificationUrl } from '@/lib/notifications';

const NOTIFICATION_POLL_INTERVAL = 60000; // 60 seconds
const SHOWN_NOTIFICATIONS_KEY = 'shown-notification-ids';

/**
 * Polls the notifications endpoint and displays them
 * as browser notifications when permission is granted.
 */
export function useNotificationPolling() {
  const { permission, showNotification } = useNotifications();
  const shownNotificationsRef = useRef<Set<string>>(
    new Set(JSON.parse(localStorage.getItem(SHOWN_NOTIFICATIONS_KEY) || '[]'))
  );

  const { data } = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => notificationsAPI.getAll(),
    refetchInterval: NOTIFICATION_POLL_INTERVAL,
    refetchIntervalInBackground: true, // Continue polling when tab is not focused
  });

  useEffect(() => {
    // Only show notifications if permission is granted and not muted by the user.
    if (permission !== 'granted') {
      return;
    }
    if (localStorage.getItem('notifications-muted') === 'true') {
      return;
    }

    const pendingNotifications = data?.notifications || [];
    if (pendingNotifications.length === 0) {
      return;
    }

    const showAll = async () => {
      for (const notification of pendingNotifications) {
        if (!notification.text) {
          continue;
        }

        const notificationKey = notification.id || notification.text;
        if (shownNotificationsRef.current.has(notificationKey)) {
          continue;
        }

        const deepLink = getNotificationUrl(notification);
        await showNotification(getNotificationLabel(notification.kind), {
          body: notification.text,
          tag: `second-brain-notification-${notificationKey}`,
          requireInteraction: false,
          // The SW's notificationclick reads data.url; give it the specific item.
          data: { url: `/#${deepLink || '/home'}` },
        });

        shownNotificationsRef.current.add(notificationKey);
      }

      localStorage.setItem(
        SHOWN_NOTIFICATIONS_KEY,
        JSON.stringify(Array.from(shownNotificationsRef.current).slice(-50))
      );
    };

    void showAll();
  }, [data, permission, showNotification]);

  const pendingNotifications = data?.notifications || [];

  return {
    hasNotification: pendingNotifications.length > 0,
    notificationCount: pendingNotifications.length,
  };
}
